import { ArrowRight, MessageCircle, PlayCircle, Clock, Users, Wrench, BookOpen } from "lucide-react";
import { config } from "@/config";

const programHighlights = [
{ icon: BookOpen, text: "Contenido estructurado por módulos" },
{ icon: PlayCircle, text: "Clases grabadas disponibles 24/7" },
{ icon: Wrench, text: "Herramientas complementarias" },
{ icon: Users, text: "Entorno de aprendizaje y comunidad" },
{ icon: Clock, text: "Acceso por tiempo (90, 180 o 360 días)" }];


export default function HeroSection() {
  const scrollToForm = () => {
    document.getElementById("formulario")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToPlans = () => {
    document.getElementById("planes")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-20 lg:pt-0 bg-[#0A1628]">

      <div className="relative min-h-screen flex items-center">
        {/* Primary blue background */}
        <div className="absolute inset-0 bg-[#0A1628]" />
        
        {/* Background Image with Blue Tint */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&auto=format&fit=crop&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.25,
            mixBlendMode: "luminosity"
          }} />

        
        {/* Blue tinted overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628]/60 via-[#0A1628]/40 to-[#112240]/60" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full py-16 md:py-24">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20 items-center">
            {/* Left Column - Copy (3 cols) */}
            <div className="lg:col-span-3 space-y-8">
              <div className="space-y-6">
                <span
                  data-testid="hero-badge"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#D4C4A8] bg-[#D4C4A8]/10 px-4 py-2 border border-[#D4C4A8]/20">

                  <span className="w-2 h-2 bg-[#D4C4A8] rounded-full animate-pulse" />
                  Ruta de Ecommerce
                </span>

                <h1
                  data-testid="hero-headline"
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] text-white"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>Una ruta más clara para aprender Ecommerce


                </h1>

                <p
                  data-testid="hero-subheadline"
                  className="text-lg md:text-xl leading-relaxed text-gray-300 max-w-2xl">Conoce una propuesta educativa de Ecommerce provista por MINED, con contenido estructurado, herramientas complementarias y un entorno de aprendizaje pensado para ayudarte a avanzar con más claridad.




                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  data-testid="hero-cta-primary"
                  onClick={scrollToForm}
                  className="group bg-white text-[#0A1628] px-8 py-4 text-base font-bold hover:bg-[#D4C4A8] transition-all duration-300 tracking-wide flex items-center justify-center gap-2">

                  Solicitar Información
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href={config.whatsapp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-secondary"
                  className="border border-white/30 text-white px-8 py-4 text-base font-medium hover:bg-white/10 hover:border-white/50 transition-all duration-300 tracking-wide flex items-center justify-center gap-2">

                  <MessageCircle size={18} />
                  Hablar por WhatsApp
                </a>
              </div>

              {/* Microdisclaimer obligatorio - versión completa */}
              <div className="pt-2 space-y-1">
                <p className="text-xs text-gray-400 max-w-xl leading-relaxed">
                  DSA actúa como embajador y canal de orientación, precalificación y acompañamiento comercial. 
                  El programa educativo es provisto por MINED.
                </p>
              </div>
            </div>

            {/* Right Column - Premium Card (2 cols) */}
            <div className="lg:col-span-2">
              <div className="relative">
                {/* Glow effect behind card */}
                <div className="absolute -inset-4 bg-[#D4C4A8]/5 blur-3xl rounded-3xl" />
                
                <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 p-8 md:p-10 rounded-sm">
                  {/* Card Header */}
                  <div className="border-b border-white/10 pb-6 mb-6">
                    <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D4C4A8] mb-3">
                      Programa de Ecommerce
                    </p>
                    <p className="text-sm text-gray-400">
                      Propuesta educativa provista por MINED
                    </p>
                  </div>

                  {/* Highlights */}
                  <div className="space-y-4 mb-8">
                    {programHighlights.map((item, i) =>
                    <div key={i} className="flex items-center gap-4 group">
                        <div className="w-10 h-10 bg-[#D4C4A8]/10 border border-[#D4C4A8]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4C4A8]/20 transition-colors">
                          <item.icon size={18} className="text-[#D4C4A8]" />
                        </div>
                        <span className="text-sm text-white/90">{item.text}</span>
                      </div>
                    )}
                  </div>

                  {/* Access Options */}
                  <div className="border-t border-white/10 pt-6 mb-6">
                    <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Modalidades de acceso</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                      { days: "90", label: "días" },
                      { days: "180", label: "días", featured: true },
                      { days: "360", label: "días" }].
                      map((plan, i) =>
                      <div
                        key={i}
                        className={`text-center py-3 px-2 border transition-all cursor-pointer ${
                        plan.featured ?
                        "border-[#D4C4A8] bg-[#D4C4A8]/10" :
                        "border-white/10 hover:border-white/20"}`
                        }>

                          <span className={`text-xl font-bold ${plan.featured ? "text-[#D4C4A8]" : "text-white/80"}`}>
                            {plan.days}
                          </span>
                          <span className={`text-xs ml-1 ${plan.featured ? "text-[#D4C4A8]/80" : "text-white/50"}`}>
                            {plan.label}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card CTA */}
                  <button
                    onClick={scrollToPlans}
                    data-testid="hero-card-cta"
                    className="w-full bg-[#D4C4A8] text-[#0A1628] py-4 text-sm font-bold hover:bg-[#C4B498] transition-colors flex items-center justify-center gap-2 tracking-wide">

                    Ver Modalidades de Acceso
                    <ArrowRight size={14} />
                  </button>

                  {/* Card disclaimer */}
                  <p className="text-[10px] text-gray-500 text-center mt-4">
                    DSA te orienta en el proceso comercial. El programa es provisto por MINED.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}
